import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addUserToGroupRequest,
  getGroupsForUserRequest,
  removeUserFromGroupRequest,
  updateUserRequest,
} from "../../../../actions";
import { UserGroupDescription, UserGroupLabel, UserGroups } from "../../../../enums/user-groups";
import { IRootReducer, UserModel } from "../../../../reducers";
import {
  CleanPhoneNumberUtil,
  ValidatePhoneNumberUtil,
} from "../../../../utils/string-utils";
import { getAllReadyOpGroupsRequest, getUserReadyOpGroupsRequest, updateUserReadyOpGroupsRequest } from "../../../../actions";

import { useAuthContext } from "../../../../core/auth-context";
import { AgencyId, JogId } from "../../../../reducers/states/agency";
import { NotificationGroup } from "../../../../reducers/states/user-info";
import ViewEditSelect from "../../../common/view-edit-select";
import ViewEditSelectAgency from "../../../common/view-edit-select-agency";
import { getJogId } from "../invite-user";
import { JogMap, GetAgencyJogs } from "../../../../reducers/states/agency/jogs";


interface EditUserProps {
  props: {
    rowCountSelected: number;
    userSelection: UserModel;
  };
}

const EditUser: FC<EditUserProps> = ({ props }) => {
  const [open, setOpen] = useState(false);
  const [userDetails, setUserDetails] = useState({} as UserModel);
  const [userRoles, setUserRoles] = useState([] as string[]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const previousJogIdRef = useRef("");
  const [userNotifGroups, setUserNotifGroups] = useState([] as string[])
  const [allNotifGroups, setAllNotifGroups] = useState([] as NotificationGroup[])
  const [agencyReadonly, setAgencyReadOnly] = useState(false);
  const [jogReadonly, setJogReadOnly] = useState(true);

  const dispatch = useDispatch();
  const userInfoStore = useSelector((state: IRootReducer) => state.userInfoReducer);
  const initialGroups = useSelector(
    (state: IRootReducer) => state.adminReducer.groupsForUser
  );
  const { authState } = useAuthContext();

  useEffect(() => {
    // Disable save if phone number is invalid. Blank is ok.
    if (userDetails.phoneNumber !== ""
      && !ValidatePhoneNumberUtil(userDetails.phoneNumber)) {
      setSaveDisabled(true);
      return;
    }

    // Disable save if any required field is blank
    setSaveDisabled(
      userDetails.firstName === "" ||
      userDetails.lastName === "" ||
      userDetails.agency === "" ||
      userDetails.jogId === "" || userDetails.jogId === JogId.Other
    );
  }, [
    userDetails.agency,
    userDetails.firstName,
    userDetails.lastName,
    userDetails.phoneNumber,
    userDetails.jogId,
  ]);

  useEffect(() => {
    setUserRoles(initialGroups);
  }, [initialGroups]);


  useEffect(() => {
    if (userInfoStore.userReadyOpGroups) {
      setUserNotifGroups(userInfoStore.userReadyOpGroups.map(g => g.id))
    }
  }, [userInfoStore.userReadyOpGroups]);


  useEffect(() => {
    if (userInfoStore.userReadyOpGroups) {
      setAllNotifGroups(userInfoStore.allReadyOpGroups)
    }
  }, [userInfoStore.allReadyOpGroups, userInfoStore.userReadyOpGroups]);

  const handleOpen = () => {
    dispatch(getGroupsForUserRequest(props.userSelection?.email));
    dispatch(getUserReadyOpGroupsRequest(props.userSelection.email));
    dispatch(getAllReadyOpGroupsRequest());

    setUserDetails(props.userSelection);
    previousJogIdRef.current = props.userSelection.jogId;

    if (authState.userGroups.toString().includes(UserGroups.AGENCY_ADMIN)
      && !authState.userGroups.toString().includes(UserGroups.SITE_ADMIN)) {      
      setAgencyReadOnly(true);
    }

    // Handles two cases: 1) Ensure JOG is editible for agencies in multiple JOGs. 2) Ensure JOG is editible if it has not been set.
    const { isReadOnly } = getJogId(props.userSelection.agency);
    const isUserJogSet = props.userSelection.jogId !== "" && props.userSelection.jogId !== JogId.Other;
    setJogReadOnly(isReadOnly && isUserJogSet);

    setOpen(true);
  };

  const handleInputChange = (e: any, field: string) => {
    let value: string = e as string;
    setUserDetails((current) => ({ ...current, [field]: value }));
  };

  const handleAgencySelectionChange = (agencyId: AgencyId, elementId: string) => {
    const { jogId, isReadOnly } = getJogId(agencyId);

    setJogReadOnly(isReadOnly);
    setUserDetails((current) => ({
      ...current,
      agency: agencyId as string,
      jogId: jogId,
    }));
  };

  const handleJogSelectionChange = (jogId: JogId, elementId: string) => {
    setUserDetails((current) => ({ ...current, jogId: jogId }));
  };

  const handleCancel = () => {
    setOpen(false);
    setAgencyReadOnly(false);
    setJogReadOnly(true);
    setUserDetails({ phoneNumber: "", firstName: "", lastName: "", agency: "", jogId: "" } as UserModel);
    setUserRoles([]);
  };

  const handleSave = () => {
    let cleanNumber = "";
    if (userDetails.phoneNumber) {
      cleanNumber = CleanPhoneNumberUtil(userDetails.phoneNumber);
    }

    let rolesToRemove = initialGroups.filter((r) => !userRoles.includes(r));
    let rolesToAdd = userRoles.filter((r) => !initialGroups.includes(r));

    dispatch(
      updateUserRequest(
        (authState.email ?? "").toLowerCase(),
        userDetails.cognitoUsername,
        userDetails.email,
        userDetails.firstName,
        userDetails.lastName,
        cleanNumber,
        userDetails.agency,
        userDetails.jogId,
        userDetails.enabled,
        userRoles,
        rolesToAdd,
        rolesToRemove,
      )
    );
    rolesToAdd.forEach((r) =>
      dispatch(addUserToGroupRequest(userDetails.email, r, (authState.email ?? "").toLowerCase()))
    );
    rolesToRemove.forEach((r) =>
      dispatch(removeUserFromGroupRequest(userDetails.email, r, (authState.email ?? "").toLowerCase()))
    );

    if (userDetails.jogId !== previousJogIdRef.current) {

      const prevJogName = JogMap.get(previousJogIdRef.current)?.name;
      const newJogName = JogMap.get(userDetails.jogId)?.name;

      const idToRemove = allNotifGroups.find(g => g.name === prevJogName)?.id || "";
      const idToAdd = allNotifGroups.find(g => g.name === newJogName)?.id || "";

      let newNotifGroups = userNotifGroups.filter(id => id !== idToRemove.toString());
      newNotifGroups.push(idToAdd);
      newNotifGroups = newNotifGroups.filter(id => id !== "");
      setUserNotifGroups(newNotifGroups);
      dispatch(updateUserReadyOpGroupsRequest(newNotifGroups, userDetails.email))
    }

    setOpen(false);
  };

  const handleRoleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    let newRoles = [...userRoles];
    if (
      event.target.checked === true &&
      newRoles.indexOf(event.target.id) === -1
    ) {
      newRoles.push(event.target.id);
    } else {
      newRoles = newRoles.filter((r) => r !== event.target.id);
    }

    setUserRoles(newRoles);
  };

  const isBtnEnabled = () => {
    return props.rowCountSelected === 1;
  }

  return (
    <>
      <Tooltip title="Edit a user in the VCC">
        <Box>
          <Button
            variant="contained"
            color={isBtnEnabled() ? "primary" : "inherit"}
            onClick={handleOpen}
            style={{ minWidth: "150px", color: isBtnEnabled() ? "white" : "black" }}
            disabled={!isBtnEnabled()}
          >
            Edit User
          </Button>
        </Box>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleCancel}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Edit User</DialogTitle>
        <DialogContent className="invite-edit-user">
          <DialogContentText>
            Update user information and roles in the VCC
          </DialogContentText>
          <TextField
            autoFocus
            required
            variant="outlined"
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            disabled
            value={userDetails.email}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="firstName"
            label="First Name"
            type="string"
            fullWidth
            value={userDetails.firstName}
            onChange={(e) => handleInputChange(e.target.value, e.target.id)}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="lastName"
            label="Last Name"
            type="string"
            fullWidth
            value={userDetails.lastName}
            onChange={(e) => handleInputChange(e.target.value, e.target.id)}
          />
          <TextField
            variant="outlined"
            margin="dense"
            id="phoneNumber"
            label="Phone Number"
            type="phoneNumber"
            fullWidth
            value={userDetails.phoneNumber}
            onChange={(e) => handleInputChange(e.target.value, e.target.id)}
          />
          <ViewEditSelectAgency
            props={{
              disableShrinkLabel: true,
              isEdit: !agencyReadonly,
              required: true,
              size: "medium",
              value: userDetails.agency,
              handleInputChange: handleAgencySelectionChange
            }}
           />
          <ViewEditSelect
            props={{
              id: "jogId",
              label: "Primary JOG",
              value: userDetails.jogId !== JogId.Other ? userDetails.jogId : undefined,
              isEdit: !jogReadonly,
              required: true,
              size: "medium",
              tooltip: "User's primary JOG. Users will receive notifications for incidents created in primary JOG.",
              handleInputChange: handleJogSelectionChange,
            }}
          >
            {GetAgencyJogs(userDetails.agency).map(
              jog => <MenuItem key={jog.id} value={jog.id}>{`${jog.name}`}</MenuItem>
            )}
          </ViewEditSelect>
          <Divider sx={{ margin: "1em 0" }} />
          <FormGroup sx={{ margin: "10px" }}>
            <Box>User Roles</Box>
            <Grid container>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox checked={true} id={UserGroups.VCC_BASIC} />
                  }
                  label={UserGroupLabel.VCC_BASIC}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.VCC_BASIC}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        userRoles.indexOf(UserGroups.INC_CONTRIBUTOR) > -1
                      }
                      id={UserGroups.INC_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        userRoles.indexOf(UserGroups.INC_MANAGER) > -1
                      }
                      id={UserGroups.INC_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        userRoles.indexOf(UserGroups.INC_RECORDS_MANAGER) >
                        -1
                      }
                      id={UserGroups.INC_RECORDS_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_RECORDS_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_RECORDS_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.PIO) > -1}
                      id={UserGroups.PIO}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.PIO}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.PIO}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.MAJOR_EVENT_CONTRIBUTOR) > -1}
                      id={UserGroups.MAJOR_EVENT_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.MAJOR_EVENT_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.MAJOR_EVENT_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.AGENCY_ADMIN) > -1}
                      id={UserGroups.AGENCY_ADMIN}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.AGENCY_ADMIN}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.AGENCY_ADMIN}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                {(authState.userGroups.indexOf(UserGroups.SITE_ADMIN) > -1) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userRoles.indexOf(UserGroups.SITE_ADMIN) > -1}
                        id={UserGroups.SITE_ADMIN}
                        onChange={handleRoleSelect}
                      />
                    }
                    label={UserGroupLabel.SITE_ADMIN}
                  />)}
              </Grid>
            </Grid>
          </FormGroup>
          <DialogActions>
            <Button onClick={handleCancel} data-rum-id="user-cancel" color="inherit" variant="contained">
              Cancel
            </Button>
            <Button
              disabled={saveDisabled}
              onClick={handleSave}
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditUser;
